/**
 * LeadTagPicker - catalog-driven chip toggle for editing a lead's tags.
 *
 * Shows the DB-owned lead_tags catalog (TD-041); clicking toggles the tag's
 * slug in the lead's tags array. Select-only: new tags come from imports, not
 * the modal. Formal credentials carry a subtle emphasis.
 */

import React from "react";
import type { LeadTag } from "../Lead.types";

interface LeadTagPickerProps {
  selectedTags: string[];
  catalog: LeadTag[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}

export const LeadTagPicker: React.FC<LeadTagPickerProps> = ({
  selectedTags,
  catalog,
  onChange,
  disabled = false,
}) => {
  const toggle = (value: string) => {
    if (disabled) return;
    onChange(
      selectedTags.includes(value)
        ? selectedTags.filter((t) => t !== value)
        : [...selectedTags, value]
    );
  };

  if (catalog.length === 0) {
    return (
      <p className="text-xs text-gray-400">
        No tags yet. Tags are created when leads are imported.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {catalog.map((tag) => {
        const isSelected = selectedTags.includes(tag.value);
        return (
          <button
            key={tag.value}
            type="button"
            onClick={() => toggle(tag.value)}
            disabled={disabled}
            title={tag.category}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              isSelected
                ? tag.isFormal
                  ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300"
                  : "bg-blue-100 text-blue-700 ring-1 ring-blue-300"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            {tag.viewValue}
          </button>
        );
      })}
    </div>
  );
};
